import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';

/**
 * Garde du site public du marchand.
 *
 * Lorsqu'un commerce désactive son site internet, tous les visiteurs sont
 * renvoyés vers la page de connexion de la boutique (la seule page encore
 * accessible), au lieu de voir le catalogue.
 */
const AUTH_PATHS = ['/login-client', '/connexion', '/register-client'];

export default function TenantSiteGate({ children }: { children: React.ReactNode }) {
  const { isTenant, tenant } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();

  const siteClosed = isTenant && !!tenant && tenant.siteEnabled === false;
  const onAuthPath = AUTH_PATHS.some((p) => location.pathname === p);

  useEffect(() => {
    if (!siteClosed || onAuthPath) return;
    navigate('/login-client', { replace: true });
  }, [siteClosed, onAuthPath, navigate]);

  if (!siteClosed) return <>{children}</>;

  if (onAuthPath) {
    return (
      <>
        <div className="w-full px-4 md:px-6 py-3 bg-secondary-100 border-b border-secondary-200/70 text-center">
          <p className="text-xs text-secondary-900">
            <i className="ri-lock-line mr-1.5"></i>
            Le site de {tenant?.nomcommerce || tenant?.name || 'ce commerce'} est momentanément fermé. Connectez-vous pour continuer.
          </p>
        </div>
        {children}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-store-2-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold font-heading text-foreground-950 mb-2">Site temporairement fermé</h1>
        <p className="text-sm text-foreground-500 mb-6">
          {tenant?.nomcommerce || tenant?.name || 'Cette boutique'} n&apos;a pas publié son site internet pour le moment.
          Vous pouvez vous connecter à votre espace client.
        </p>
        <button
          type="button"
          onClick={() => navigate('/login-client', { replace: true })}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-login-box-line"></i>
          Se connecter
        </button>
      </div>
    </div>
  );
}